import type { GameEvent } from '@/types'
import { useAPI } from '@/utils/requests/api-plugin'
import type { FetchRequest } from '@/utils/requests/types'

import { MessageAction, type OutcomingMessage } from '../main-frame/message-types'
import { createIdbApi } from '../safe-storage'

const SAFE_STORAGE_TIMEOUT = 5000 // 5s

export type SerializableState = Record<string, string | number | boolean>
export type CloudSaveMessageHandler = (event: GameEvent) => Promise<OutcomingMessage | undefined>

export const useCloudSave = () => {
    const api = useAPI()
    const waitTimeout = <T>(msg: T) => new Promise<T>((res) => setTimeout(() => res(msg), SAFE_STORAGE_TIMEOUT))
    type RequestParams = Parameters<FetchRequest>
    const requestWithTimeout = <T>(url: RequestParams[0], opts?: RequestParams[1]) =>
        Promise.race([
            api.request<T>(url, opts),
            waitTimeout({ originalResponse: undefined, data: null, errors: ['Client timeout'] }),
        ])

    const getCloudSaveApi = (gameId: string) => ({
        getState: async () => {
            const state = await requestWithTimeout<{ saveData: Record<string, string> }>(
                `/api/v1/cloud-save/games/${encodeURIComponent(gameId)}/load`,
            )

            if (state?.errors?.length) {
                console.error(state.errors)
                return {}
            }

            return state.data?.saveData || {}
        },
        setItems: async (state: SerializableState) => {
            const resp = await requestWithTimeout(`/api/v1/cloud-save/games/${encodeURIComponent(gameId)}/save`, {
                method: 'POST',
                body: JSON.stringify({ saveData: state }),
            })

            return !resp.errors?.length
        },
    })
    const createCloudSave = (
        gameId: string,
        gameHru: string,
        onSave: (data: { type: 'cloud-save'; action: MessageAction }) => void,
    ) => {
        const { getState, setItems } = getCloudSaveApi(gameId)

        const messageHandler = async (event: GameEvent) => {
            const createResponse = (
                action: MessageAction,
                payload: { state: SerializableState } | { success: boolean; error?: string },
            ) =>
                ({
                    type: 'cloud-save',
                    originalMessageEvent: event as GameEvent,
                    payload,
                    action,
                }) as OutcomingMessage
            switch (event.data.action) {
                case MessageAction.getCloudSaveState: {
                    let state = await getState()

                    // If cloud save is empty, try to get state from indexedDB
                    if (Object.keys(state).length === 0) {
                        const { getState: getIdbState } = createIdbApi(gameHru)
                        const localState = await getIdbState()
                        state = localState
                    }
                    return createResponse(MessageAction.cloudSaveStateResponse, { state })
                }
                case MessageAction.setCloudSaveState: {
                    onSave({ type: 'cloud-save', action: MessageAction.setCloudSaveState })
                    try {
                        const success = await setItems(event.data.payload as SerializableState)
                        return createResponse(MessageAction.cloudSaveOperationResponse, { success })
                    } catch (e) {
                        console.error(e)
                        const error = (e as Error).toString()
                        return createResponse(MessageAction.cloudSaveOperationResponse, { success: false, error })
                    }
                }
                default:
            }
            return undefined
        }

        return messageHandler
    }
    return {
        createCloudSave,
        getCloudSaveApi,
    }
}
